import React,{useEffect,useRef,useState} from 'react';
import {createPortal} from 'react-dom';
import {Barcode,Loader2,X} from 'lucide-react';
import {supportedRetailFormats,validateScannedCode} from './posBarcodeProfile.js';

// POS-only live scanner. Does not replace the proven photo camera in the
// catalogue. A scan only populates the POS search, never submits a sale.
export default function PosLiveBarcodeScanner({onDetected,onClose}){
  const [status,setStatus]=useState('opening');
  const [error,setError]=useState('');
  const videoRef=useRef(null);
  const controlsRef=useRef(null);
  const onDetectedRef=useRef(onDetected);
  const onCloseRef=useRef(onClose);
  onDetectedRef.current=onDetected;
  onCloseRef.current=onClose;

  useEffect(()=>{
    let cancelled=false;
    let captured=false;
    let fallbackStarted=false;
    let nativeTimer=null;
    let controls;
    const video=videoRef.current;
    const stop=()=>{
      if(nativeTimer!==null){clearTimeout(nativeTimer);nativeTimer=null;}
      controls?.stop?.();
      controlsRef.current?.stop?.();
      controlsRef.current=null;
      const stream=video?.srcObject;
      stream?.getTracks?.().forEach(track=>track.stop());
      if(video)video.srcObject=null;
    };
    function accept(raw){
      if(cancelled||captured)return;
      const value=validateScannedCode(raw);
      if(!value.valid){
        if(value.reason==='gtin')setError('Código detectado com dígito inválido. Ajuste o enquadramento.');
        return;
      }
      captured=true;
      stop();
      try{navigator.vibrate?.(70)}catch{}
      onDetectedRef.current?.(value.code);
      onCloseRef.current?.();
    }
    const keydown=e=>{if(e.key==='Escape')onCloseRef.current?.()};
    document.addEventListener('keydown',keydown);

    async function startZxing(){
      if(cancelled||captured||fallbackStarted)return;
      fallbackStarted=true;
      if(nativeTimer!==null){clearTimeout(nativeTimer);nativeTimer=null;}
      try{
        // Only retail 1D symbols; avoids trying QR/Aztec/Data Matrix at
        // each frame and keeps the existing, tested ZXing dependency.
        const {BrowserMultiFormatOneDReader}=await import('@zxing/browser');
        if(cancelled||captured)return;
        const reader=new BrowserMultiFormatOneDReader(undefined,{
          delayBetweenScanAttempts:95,
          delayBetweenScanSuccess:400,
        });
        controls=await reader.decodeFromVideoElement(video,result=>{
          if(result)accept(result.getText?.());
        });
        if(cancelled||captured)controls?.stop?.();
        else controlsRef.current=controls;
      }catch{
        if(cancelled||captured)return;
        setStatus('error');
        setError('Não foi possível iniciar a leitura. Volte para digitar o código na busca.');
      }
    }

    async function begin(){
      if(!window.isSecureContext||!navigator.mediaDevices?.getUserMedia){
        setStatus('error');
        setError('Câmera indisponível neste navegador. Feche o leitor e digite o código na busca.');
        return;
      }
      try{
        // Higher resolution improves small EAN labels. Constraints are
        // preferences, so devices without 1280px can select a lower mode.
        const stream=await navigator.mediaDevices.getUserMedia({
          audio:false,
          video:{facingMode:{ideal:'environment'},
            width:{ideal:1280},height:{ideal:720},frameRate:{ideal:30}}
        });
        if(cancelled){stream.getTracks().forEach(track=>track.stop());return;}
        video.srcObject=stream;
        await video.play();
        if(cancelled||captured)return;
        setStatus('ready');
        const Detector=window.BarcodeDetector;
        if(typeof Detector!=='function'){
          await startZxing();return;
        }
        let detector;
        try{
          const supported=await Detector.getSupportedFormats();
          const formats=supportedRetailFormats(supported);
          if(!formats.length){await startZxing();return;}
          detector=new Detector({formats});
        }catch{
          await startZxing();return;
        }
        if(cancelled||captured)return;
        const deadline=Date.now()+1800;
        async function scanNative(){
          if(cancelled||captured||fallbackStarted)return;
          if(Date.now()>deadline){await startZxing();return;}
          try{
            if(video.readyState>=2){
              const findings=await detector.detect(video);
              for(const found of findings||[]){
                if(cancelled||captured)return;
                const value=validateScannedCode(found.rawValue);
                if(value.valid){accept(value.code);return;}
              }
            }
          }catch{
            await startZxing();return;
          }
          if(!cancelled&&!captured&&!fallbackStarted)
            nativeTimer=setTimeout(scanNative,110);
        }
        await scanNative();
      }catch(err){
        if(cancelled||captured)return;
        setStatus('error');
        const denied=err?.name==='NotAllowedError'||err?.name==='PermissionDeniedError';
        setError(denied?'Permissão da câmera bloqueada. Autorize no Chrome ou digite o código na busca.':
          'Não foi possível iniciar a câmera. Feche o leitor e digite o código na busca.');
      }
    }
    begin();
    return()=>{cancelled=true;document.removeEventListener('keydown',keydown);stop();};
  },[]);

  return createPortal(
    <div className="posLiveScanner" role="dialog" aria-modal="true" aria-label="Leitor automático de código de barras">
      <div className="posLiveScannerTop">
        <button type="button" aria-label="Fechar leitor e voltar ao PDV"
          onClick={()=>onCloseRef.current?.()}><X size={26}/></button>
        <strong>Escanear código</strong>
        <span aria-hidden="true"/>
      </div>
      <div className="posLiveScannerView">
        <video ref={videoRef} autoPlay playsInline muted aria-label="Câmera para identificar código de barras"/>
        <div className="posLiveScannerTarget" aria-hidden="true">
          <span/><span/><span/><span/>
        </div>
        {status==='opening'&&<div className="posLiveScannerLoading" role="status">
          <Loader2 size={22} className="spin"/> Abrindo a câmera…
        </div>}
      </div>
      <div className="posLiveScannerBottom">
        <Barcode size={23} aria-hidden="true"/>
        <p>Centralize o código na área clara. A leitura e o preenchimento são automáticos.</p>
        {error&&<p className="posLiveScannerError" role="alert">{error}</p>}
        <button type="button" onClick={()=>onCloseRef.current?.()}>Voltar e digitar</button>
      </div>
    </div>,document.body
  );
}
